import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useData } from '../contexts/DataContext';

import StatusBadge from '../components/ui/StatusBadge';
import CapacityBar, { CapacityBarMini } from '../components/ui/CapacityBar';
import { 
  Users, 
  Plus,
  Search,
  Filter,
  Calendar,
  CheckSquare,
  User,
  FolderOpen,
  X,
  Edit,
  Trash2,
  Building2,
  Clock,
  Check  // הוסף את זה אם חסר
} from 'lucide-react';
import { db, COLLECTIONS } from '../firebase/config';
import { collection, addDoc, serverTimestamp, updateDoc, doc, deleteDoc } from 'firebase/firestore';
const EmployeeSearchField = ({ 
  employees = [], 
  selectedEmployees = [], 
  onEmployeeSelect,
  onEmployeeRemove,
  placeholder = "חפש עובדים...",
  multiple = true,
  showDepartment = false
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const dropdownRef = useRef(null);

  // Filter employees based on search term
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredEmployees([]);
      return;
    }

    const filtered = employees.filter(employee => {
      const searchLower = searchTerm.toLowerCase();
      return (
        employee.name?.toLowerCase().includes(searchLower) ||
        employee.email?.toLowerCase().includes(searchLower) ||
        employee.role?.toLowerCase().includes(searchLower)
      );
    }).filter(emp => !selectedEmployees.find(selected => selected.id === emp.id));

    setFilteredEmployees(filtered);
  }, [searchTerm, employees, selectedEmployees]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleEmployeeSelect = (employee) => {
    onEmployeeSelect(employee);
    setSearchTerm('');
    setIsOpen(false);
    if (!multiple) {
      setFilteredEmployees([]);
    }
  };

  const handleRemoveEmployee = (employeeId) => {
    onEmployeeRemove(employeeId);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Selected Employees */}
      {selectedEmployees.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-2">
          {selectedEmployees.map(employee => (
            <div
              key={employee.id}
              className="flex items-center gap-2 bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm"
            >
              <User size={14} />
              <span>{employee.name}</span>
              <button
                type="button"
                onClick={() => handleRemoveEmployee(employee.id)}
                className="hover:text-blue-600 transition-colors"
              >
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Search Input */}
      <div className="relative">
        <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
          <Search className="h-5 w-5 text-gray-400" />
        </div>
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          placeholder={placeholder}
          className="w-full pr-10 pl-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      {/* Dropdown Results */}
      {isOpen && searchTerm && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {filteredEmployees.length === 0 ? (
            <div className="px-4 py-3 text-gray-500 text-center">
              לא נמצאו עובדים
            </div>
          ) : (
            filteredEmployees.map(employee => (
              <button
                key={employee.id}
                type="button"
                onClick={() => handleEmployeeSelect(employee)}
                className="w-full px-4 py-3 hover:bg-gray-50 flex items-center justify-between transition-colors text-right"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                    <User size={16} className="text-gray-600" />
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">{employee.name}</div>
                    <div className="text-sm text-gray-500">{employee.email}</div>
                    {employee.role && (
                      <div className="text-xs text-gray-400">
                        {(() => {
                          switch (employee.role) {
                            case 'org_admin':
                              return 'מנהל ראשי';
                            case 'ceo':
                              return 'מנכ"ל';
                            case 'deputy_ceo':
                              return 'סמנכ"ל';
                            case 'dept_manager':
                              return 'מנהל מחלקה';
                            case 'team_manager':
                              return 'מנהל צוות';
                            case 'employee':
                              return 'עובד';
                            default:
                              return employee.role;
                          }
                        })()}
                      </div>
                    )}
                    {showDepartment && employee.departmentName && (
                      <div className="text-xs text-blue-500">{employee.departmentName}</div>
                    )}
                  </div>
                </div>
                {selectedEmployees.find(e => e.id === employee.id) && (
                  <Check size={16} className="text-green-600" />
                )}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};
const Departments = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { departments, users, tasks, projects, updateUser, createDepartment, updateDepartment, deleteDepartment } = useData();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showDepartmentModal, setShowDepartmentModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingDepartment, setEditingDepartment] = useState(null);
  const [departmentForm, setDepartmentForm] = useState({
    name: '',
    description: '',
    managerIds: [],
    teamMembers: [],
    isActive: true
  });

  // Filter departments based on user role, search and filters
  const filteredDepartments = departments.filter(department => {
    // First filter by user role
    let hasAccess = false;
    
    if (currentUser?.role === 'org_admin') {
      // Org admin sees all departments
      hasAccess = true;
    } else if (currentUser?.role === 'ceo' || currentUser?.role === 'deputy_ceo') {
      // CEO and Deputy CEO see all departments created by CEO, Deputy CEO, or org_admin
      hasAccess = department.createdBy === currentUser.id || 
                  users.find(u => u.id === department.createdBy)?.role === 'ceo' ||
                  users.find(u => u.id === department.createdBy)?.role === 'deputy_ceo' ||
                  users.find(u => u.id === department.createdBy)?.role === 'org_admin';
    } else if (currentUser?.role === 'dept_manager') {
      // Department manager sees their own department and departments they created
      hasAccess = department.id === currentUser.departmentId || 
                  department.createdBy === currentUser.id ||
                  department.managerId === currentUser.id;
    } else {
      // Employees don't have access to departments page, but if they do, show only their department
      hasAccess = department.id === currentUser?.departmentId;
    }
    
    if (!hasAccess) return false;
    
    // Then apply search and status filters
    const matchesSearch = department.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         department.description?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filterStatus === 'all' || 
                         (filterStatus === 'active' && department.isActive) ||
                         (filterStatus === 'inactive' && !department.isActive);
    
    return matchesSearch && matchesStatus;
  });

  // Reset department form
  const resetDepartmentForm = () => {
    setDepartmentForm({
      name: '',
      description: '',
      managerIds: [],
      teamMembers: [],
      isActive: true
    });
  };

  // Handle create department
  const handleCreateDepartment = async (e) => {
    e.preventDefault();
    try {
      const departmentData = {
        name: departmentForm.name,
        description: departmentForm.description,
        managerIds: departmentForm.managerIds,
        teamMembers: departmentForm.teamMembers,
        isActive: departmentForm.isActive ?? true
      };
      
      // Use createDepartment from DataContext which handles teamMembers automatically
      const newDepartmentId = await createDepartment(departmentData);
      
      // If managers are specified, assign them to the department
      if (departmentForm.managerIds && departmentForm.managerIds.length > 0) {
        for (const managerId of departmentForm.managerIds) {
          try {
            if (updateUser) {
              await updateUser(managerId, { departmentId: newDepartmentId });
            }
          } catch (updateError) {
            console.error('Error assigning manager to department:', updateError);
          }
        }
      }
      // If current user is a dept_manager and they created a department without specifying managers, update their departmentId
      else if (currentUser.role === 'dept_manager') {
        try {
          // Update the current user's departmentId to the new department
          if (updateUser) {
            await updateUser(currentUser.id, { departmentId: newDepartmentId });
          }
        } catch (updateError) {
          console.error('Error updating user department:', updateError);
        }
      }
      
      setShowDepartmentModal(false);
      resetDepartmentForm();
      
      // Show success message
      if (departmentForm.managerIds && departmentForm.managerIds.length > 0) {
        const managerNames = departmentForm.managerIds.map(id => 
          users.find(u => u.id === id)?.name || 'לא ידוע'
        ).join(', ');
        alert(`המחלקה "${departmentForm.name}" נוצרה בהצלחה! ${managerNames} מוגדרים כמנהלי המחלקה.`);
      } else if (currentUser.role === 'dept_manager') {
        alert(`המחלקה "${departmentForm.name}" נוצרה בהצלחה! אתה מוגדר כמנהל המחלקה החדשה.`);
      } else {
        alert(`המחלקה "${departmentForm.name}" נוצרה בהצלחה!`);
      }
      
      // הוסף הודעה על העובדים שנוספו
      if (departmentForm.teamMembers && departmentForm.teamMembers.length > 0) {
        const teamMemberNames = departmentForm.teamMembers.map(id => 
          users.find(u => u.id === id)?.name || 'לא ידוע'
        ).join(', ');
        alert(`העובדים הבאים נוספו למחלקה: ${teamMemberNames}`);
      }
    } catch (error) {
      console.error('Error creating department:', error);
      alert('שגיאה ביצירת המחלקה');
    }
  };

  // Handle edit department
  const handleEditDepartment = (department) => {
    setEditingDepartment(department);
    setDepartmentForm({
      name: department.name,
      description: department.description || '',
      managerIds: department.managerIds || (department.managerId ? [department.managerId] : []),
      teamMembers: department.teamMembers || [],
      isActive: department.isActive ?? true
    });
    setShowEditModal(true);
  };

  // Handle update department
  const handleUpdateDepartment = async (e) => {
    e.preventDefault();
    try {
      const departmentData = {
        name: departmentForm.name,
        description: departmentForm.description,
        managerIds: departmentForm.managerIds,
        teamMembers: departmentForm.teamMembers,
        isActive: departmentForm.isActive
      };
      
      // Get the previous manager IDs to compare
      const previousManagerIds = editingDepartment.managerIds || (editingDepartment.managerId ? [editingDepartment.managerId] : []);
      const newManagerIds = departmentForm.managerIds || [];
      
      // Use updateDepartment from DataContext which handles teamMembers automatically
      await updateDepartment(editingDepartment.id, departmentData);
      
      // Handle manager changes
      const managersToRemove = previousManagerIds.filter(id => !newManagerIds.includes(id));
      const managersToAdd = newManagerIds.filter(id => !previousManagerIds.includes(id));
      
      // Remove previous managers' department assignment
      for (const managerId of managersToRemove) {
        try {
          if (updateUser) {
            await updateUser(managerId, { departmentId: null });
          }
        } catch (error) {
          console.error('Error removing manager from department:', error);
        }
      }
      
      // Assign new managers to this department
      for (const managerId of managersToAdd) {
        try {
          if (updateUser) {
            await updateUser(managerId, { departmentId: editingDepartment.id });
          }
        } catch (error) {
          console.error('Error assigning manager to department:', error);
        }
      }
      
      setShowEditModal(false);
      setEditingDepartment(null);
      resetDepartmentForm();
      alert('המחלקה עודכנה בהצלחה!');
      
      // הוסף הודעה על העובדים שעודכנו
      const previousTeamMembers = editingDepartment.teamMembers || [];
      const newTeamMembers = departmentForm.teamMembers || [];
      const addedMembers = newTeamMembers.filter(id => !previousTeamMembers.includes(id));
      const removedMembers = previousTeamMembers.filter(id => !newTeamMembers.includes(id));
      
      if (addedMembers.length > 0) {
        const addedNames = addedMembers.map(id => 
          users.find(u => u.id === id)?.name || 'לא ידוע'
        ).join(', ');
        alert(`העובדים הבאים נוספו למחלקה: ${addedNames}`);
      }
      
      if (removedMembers.length > 0) {
        const removedNames = removedMembers.map(id => 
          users.find(u => u.id === id)?.name || 'לא ידוע'
        ).join(', ');
        alert(`העובדים הבאים הוסרו מהמחלקה: ${removedNames}`);
      }
    } catch (error) {
      console.error('Error updating department:', error);
      alert('שגיאה בעדכון המחלקה');
    }
  };

  // Handle delete department
  const handleDeleteDepartment = async (department) => {
    if (window.confirm(`האם אתה בטוח שברצונך למחוק את המחלקה "${department.name}"?`)) {
      try {
        // Use deleteDepartment from DataContext which handles removing departmentId from users
        await deleteDepartment(department.id);
        alert(`המחלקה "${department.name}" נמחקה בהצלחה!`);
      } catch (error) {
        console.error('Error deleting department:', error);
        alert('שגיאה במחיקת המחלקה');
      }
    }
  };

  // Calculate department statistics
  const getDepartmentStats = (department) => {
    const deptUsers = users.filter(u => u.departmentId === department.id);
    const deptTasks = tasks.filter(t => t.departmentId === department.id);
    const deptProjects = projects.filter(p => p.departmentId === department.id);
    
    return {
      userCount: deptUsers.length,
      taskCount: deptTasks.length,
      projectCount: deptProjects.length,
      completedTasks: deptTasks.filter(t => t.status === 'הושלם').length,
      activeTasks: deptTasks.filter(t => t.status !== 'הושלם').length
    };
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold">מחלקות</h1>
          {(currentUser?.role === 'org_admin' || currentUser?.role === 'ceo' || currentUser?.role === 'deputy_ceo' || currentUser?.role === 'dept_manager') && (
            <button
              onClick={() => {
                setShowDepartmentModal(true);
              }}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              <Plus size={20} />
              מחלקה חדשה
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex flex-wrap gap-4">
          {/* Search */}
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute right-3 top-3 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="חיפוש מחלקות..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pr-10 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
              />
            </div>
          </div>

          {/* Status Filter */}
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
          >
            <option value="all">כל הסטטוסים</option>
            <option value="active">פעילות</option>
            <option value="inactive">לא פעילות</option>
          </select>
        </div>
      </div>

      {/* Departments Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredDepartments.map(department => {
          const stats = getDepartmentStats(department);
          
          return (
            <div
              key={department.id}
              onClick={() => navigate(`/departments/${department.id}`)}
              className="group relative bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer p-6 border border-gray-100 hover:border-blue-200 hover:scale-[1.02] overflow-hidden"
            >
              {/* Background Pattern */}
              <div className="absolute inset-0 bg-gradient-to-br from-blue-50/30 to-purple-50/30 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-0"></div>
              
              {/* Status Badge */}
              <div className="absolute top-4 right-4 z-10">
                <span className={`px-3 py-1.5 rounded-full text-xs font-medium shadow-sm whitespace-nowrap ${
                  department.isActive 
                    ? 'bg-gradient-to-r from-green-400 to-emerald-500 text-white' 
                    : 'bg-gradient-to-r from-red-400 to-pink-500 text-white'
                }`}>
                  {department.isActive ? 'פעילה' : 'לא פעילה'}
                </span>
              </div>

              {/* Action buttons - only for org_admin */}
              {currentUser?.role === 'org_admin' && (
                <div className="absolute top-4 left-4 flex gap-2 opacity-0 group-hover:opacity-100 transition-all duration-300 z-10">
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditDepartment(department);
                    }}
                    className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition-all duration-200 flex-shrink-0"
                    title="ערוך מחלקה"
                  >
                    <Edit size={16} />
                  </button>
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteDepartment(department);
                    }}
                    className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-xl transition-all duration-200 flex-shrink-0"
                    title="מחק מחלקה"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              )}

              {/* Header */}
              <div className="relative z-10 mb-8 pt-12">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg flex-shrink-0">
                    <Building2 size={20} className="text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-xl text-gray-800 mb-1 group-hover:text-blue-600 transition-colors duration-200 break-words">
                      {department.name}
                    </h3>
                    <p className="text-gray-600 text-sm leading-relaxed line-clamp-2">
                      {department.description || 'אין תיאור זמין למחלקה זו'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="relative z-10 space-y-5">
                {/* Manager Info */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-3 border border-blue-100">
                  <div className="flex items-center gap-2 text-sm">
                    <div className="w-6 h-6 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <User size={14} className="text-blue-600" />
                    </div>
                    <span className="text-blue-800 font-medium">
                      מנהלים: {(() => {
                        const managerIds = department.managerIds || (department.managerId ? [department.managerId] : []);
                        if (managerIds.length === 0) return 'לא מוגדרים';
                        
                        const managerNames = managerIds.map(id =>
                          users.find(u => u.id === id)?.name || 'לא ידוע'
                        ).join(', ');
                        
                        return managerNames;
                      })()}
                    </span>
                  </div>
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-1 gap-3">
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-3 border border-green-100">
                    <div className="flex items-center gap-3">
                      <div className="w-6 h-6 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Users size={14} className="text-green-600" />
                      </div>
                      <div className="flex-1">
                        <p className="text-2xl font-bold text-green-700">{stats.userCount}</p>
                        <p className="text-xs text-green-600">עובדים</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-gradient-to-r from-purple-50 to-violet-50 rounded-xl p-3 border border-purple-100">
                    <div className="flex items-center gap-3">
                      <div className="w-6 h-6 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <CheckSquare size={14} className="text-purple-600" />
                      </div>
                      <div className="flex-1">
                        <p className="text-2xl font-bold text-purple-700">{stats.taskCount}</p>
                        <p className="text-xs text-purple-600">משימות</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl p-3 border border-orange-100">
                    <div className="flex items-center gap-3">
                      <div className="w-6 h-6 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <FolderOpen size={14} className="text-orange-600" />
                      </div>
                      <div className="flex-1">
                        <p className="text-2xl font-bold text-orange-700">{stats.projectCount}</p>
                        <p className="text-xs text-orange-600">פרויקטים</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-gradient-to-r from-teal-50 to-cyan-50 rounded-xl p-3 border border-teal-100">
                    <div className="flex items-center gap-3">
                      <div className="w-6 h-6 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Calendar size={14} className="text-teal-600" />
                      </div>
                      <div className="flex-1">
                        <p className="text-2xl font-bold text-teal-700">{stats.completedTasks}</p>
                        <p className="text-xs text-teal-600">הושלמו</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Progress Bar */}
                {stats.taskCount > 0 && (
                  <div className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-xl p-3 border border-gray-100">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-medium text-gray-700">התקדמות משימות</span>
                      <span className="text-sm font-bold text-blue-600">{stats.completedTasks}/{stats.taskCount}</span>
                    </div>
                    <CapacityBarMini 
                      current={stats.completedTasks}
                      total={stats.taskCount}
                    />
                  </div>
                )}

                {/* Active Tasks */}
                {stats.activeTasks > 0 && (
                  <div className="bg-gradient-to-r from-orange-50 to-red-50 rounded-xl p-3 border border-orange-100">
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Clock size={12} className="text-orange-600" />
                      </div>
                      <span className="text-sm font-medium text-orange-700 truncate">
                        {stats.activeTasks} משימות פעילות
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Hover Effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl"></div>
            </div>
          );
        })}
      </div>

      {/* Empty State */}
      {filteredDepartments.length === 0 && (
        <div className="text-center py-12">
          <Users size={48} className="mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {searchTerm || filterStatus !== 'all' ? 'לא נמצאו מחלקות' : 'אין מחלקות עדיין'}
          </h3>
          <p className="text-gray-500 mb-4">
            {searchTerm || filterStatus !== 'all' 
              ? 'נסה לשנות את החיפוש או הפילטרים שלך'
              : 'התחל ליצור מחלקות כדי לארגן את העובדים והפרויקטים שלך'
            }
          </p>
          {(currentUser?.role === 'org_admin' || currentUser?.role === 'dept_manager') && !searchTerm && filterStatus === 'all' && (
            <button
              onClick={() => {
                setShowDepartmentModal(true);
              }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              <Plus size={20} />
              צור מחלקה ראשונה
            </button>
          )}
        </div>
      )}

      {/* Department Modal */}
      {showDepartmentModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => {
            setShowDepartmentModal(false);
            resetDepartmentForm();
          }}
        >
          <div 
            className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-green-600 px-6 py-4 flex items-center justify-between">
              <button
                onClick={() => {
                  setShowDepartmentModal(false);
                  resetDepartmentForm();
                }}
                className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center hover:bg-opacity-30 transition-all"
              >
                <X className="w-5 h-5 text-white" />
              </button>
              <div className="text-center">
                <h3 className="text-xl font-semibold text-white">מחלקה חדשה</h3>
                <p className="text-green-100 text-sm">צור מחלקה חדשה עם כל הפרטים הנדרשים</p>
              </div>
              <button
                type="submit"
                form="newDepartmentForm"
                className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center hover:bg-opacity-30 transition-all"
              >
                <Plus className="w-5 h-5 text-white" />
              </button>
            </div>

            {/* Form */}
            <form id="newDepartmentForm" onSubmit={handleCreateDepartment} className="p-6 overflow-y-auto max-h-[60vh]">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Left Column */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <Users className="w-4 h-4 text-gray-500" />
                      שם מחלקה
                    </label>
                    <input
                      type="text"
                      value={departmentForm.name}
                      onChange={(e) => setDepartmentForm({ ...departmentForm, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all"
                      placeholder="הכנס שם המחלקה"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <FolderOpen className="w-4 h-4 text-gray-500" />
                      תיאור (אופציונלי)
                    </label>
                    <textarea
                      value={departmentForm.description}
                      onChange={(e) => setDepartmentForm({ ...departmentForm, description: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all h-24 resize-none"
                      placeholder="תאר את המחלקה בקצרה..."
                      rows="3"
                    />
                  </div>
                </div>

                {/* Right Column */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <User className="w-4 h-4 text-gray-500" />
                      מנהלי מחלקה (אופציונלי)
                    </label>
                    <div className="border border-gray-300 rounded-lg p-3 max-h-[120px] overflow-y-auto bg-gray-50">
                      {users.filter(user => user.role === 'dept_manager' || user.role === 'org_admin' || user.role === 'ceo' || user.role === 'deputy_ceo' || user.role === 'team_manager').map((user) => (
                        <label key={user.id} className="flex items-center gap-3 p-2 hover:bg-white rounded cursor-pointer">
                          <input
                            type="checkbox"
                            checked={departmentForm.managerIds?.includes(user.id) || false}
                            onChange={(e) => {
                              if (e.target.checked) {
                                // הוסף מנהל
                                setDepartmentForm(prevState => ({
                                  ...prevState,
                                  managerIds: [...(prevState.managerIds || []), user.id]
                                }));
                              } else {
                                // הסר מנהל
                                setDepartmentForm(prevState => ({
                                  ...prevState,
                                  managerIds: (prevState.managerIds || []).filter(id => id !== user.id)
                                }));
                              }
                            }}
                            className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                          />
                          <span className="text-sm text-gray-700">
                            {user.name} ({(() => {
                              switch (user.role) {
                                case 'org_admin':
                                  return 'מנהל ראשי';
                                case 'ceo':
                                  return 'מנכ"ל';
                                case 'deputy_ceo':
                                  return 'סמנכ"ל';
                                case 'dept_manager':
                                  return 'מנהל מחלקה';
                                case 'team_manager':
                                  return 'מנהל צוות';
                                default:
                                  return 'עובד';
                              }
                            })()})
                          </span>
                        </label>
                      ))}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      לחץ על כל מנהל שברצונך להוסיף
                      <br />
                      <span className="text-blue-600">נבחרו: {departmentForm.managerIds?.length || 0} מנהלים</span>
                    </p>
                  </div>

                 <div>
  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
    <Users className="w-4 h-4 text-gray-500" />
    חברי צוות (אופציונלי)
  </label>
  <EmployeeSearchField
    employees={users.filter(user => user.role === 'employee' && user.isActive).map(user => ({
      ...user,
      departmentName: departments.find(d => d.id === user.departmentId)?.name
    }))}
    selectedEmployees={users.filter(user => departmentForm.teamMembers?.includes(user.id))}
    onEmployeeSelect={(employee) => {
      setDepartmentForm(prevState => ({
        ...prevState,
        teamMembers: [...(prevState.teamMembers || []), employee.id]
      }));
    }}
    onEmployeeRemove={(employeeId) => {
      setDepartmentForm(prevState => ({
        ...prevState,
        teamMembers: (prevState.teamMembers || []).filter(id => id !== employeeId)
      }));
    }}
    placeholder="חפש עובדים להוספה למחלקה..."
    showDepartment={true}
  />
</div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="mt-8 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowDepartmentModal(false);
                    resetDepartmentForm();
                  }}
                  className="px-6 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  בטל
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  צור מחלקה
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Department Modal */}
      {showEditModal && editingDepartment && (
        <div key={`edit-modal-${editingDepartment.id}-${Math.random()}`}>
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => {
            setShowEditModal(false);
            setEditingDepartment(null);
            resetDepartmentForm();
          }}
        >
          <div 
            className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-blue-600 px-6 py-4 flex items-center justify-between">
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setEditingDepartment(null);
                  resetDepartmentForm();
                }}
                className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center hover:bg-opacity-30 transition-all"
              >
                <X className="w-5 h-5 text-white" />
              </button>
              <div className="text-center">
                <h3 className="text-xl font-semibold text-white">ערוך מחלקה</h3>
                <p className="text-blue-100 text-sm">עדכן את פרטי המחלקה</p>
              </div>
              <button
                type="submit"
                form="editDepartmentForm"
                className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center hover:bg-opacity-30 transition-all"
              >
                <Edit className="w-5 h-5 text-white" />
              </button>
            </div>

            {/* Form */}
            <form id="editDepartmentForm" onSubmit={handleUpdateDepartment} className="p-6 overflow-y-auto max-h-[60vh]">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Left Column */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <Users className="w-4 h-4 text-gray-500" />
                      שם מחלקה
                    </label>
                    <input
                      type="text"
                      value={departmentForm.name}
                      onChange={(e) => setDepartmentForm({ ...departmentForm, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                      placeholder="הכנס שם המחלקה"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <FolderOpen className="w-4 h-4 text-gray-500" />
                      תיאור (אופציונלי)
                    </label>
                    <textarea
                      value={departmentForm.description}
                      onChange={(e) => setDepartmentForm({ ...departmentForm, description: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all h-24 resize-none"
                      placeholder="תאר את המחלקה בקצרה..."
                      rows="3"
                    />
                  </div>

                  <div>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={departmentForm.isActive}
                        onChange={(e) => setDepartmentForm({ ...departmentForm, isActive: e.target.checked })}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm font-medium text-gray-700">מחלקה פעילה</span>
                    </label>
                  </div>
                </div>

                {/* Right Column */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <User className="w-4 h-4 text-gray-500" />
                      מנהלי מחלקה (אופציונלי)
                    </label>
                    <div className="border border-gray-300 rounded-lg p-3 max-h-[120px] overflow-y-auto bg-gray-50">
                      {users.filter(user => user.role === 'dept_manager' || user.role === 'org_admin' || user.role === 'ceo' || user.role === 'deputy_ceo' || user.role === 'team_manager').map((user) => (
                        <label key={user.id} className="flex items-center gap-3 p-2 hover:bg-white rounded cursor-pointer">
                          <input
                            type="checkbox"
                            checked={departmentForm.managerIds?.includes(user.id) || false}
                            onChange={(e) => {
                              if (e.target.checked) {
                                // הוסף מנהל
                                setDepartmentForm(prevState => ({
                                  ...prevState,
                                  managerIds: [...(prevState.managerIds || []), user.id]
                                }));
                              } else {
                                // הסר מנהל
                                setDepartmentForm(prevState => ({
                                  ...prevState,
                                  managerIds: (prevState.managerIds || []).filter(id => id !== user.id)
                                }));
                              }
                            }}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="text-sm text-gray-700">
                            {user.name} ({(() => {
                              switch (user.role) {
                                case 'org_admin':
                                  return 'מנהל ראשי';
                                case 'ceo':
                                  return 'מנכ"ל';
                                case 'deputy_ceo':
                                  return 'סמנכ"ל';
                                case 'dept_manager':
                                  return 'מנהל מחלקה';
                                case 'team_manager':
                                  return 'מנהל צוות';
                                default:
                                  return 'עובד';
                              }
                            })()})
                          </span>
                        </label>
                      ))}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      לחץ על כל מנהל שברצונך להוסיף
                      <br />
                      <span className="text-blue-600">נבחרו: {departmentForm.managerIds?.length || 0} מנהלים</span>
                    </p>
                  </div>

                 <div>
  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
    <Users className="w-4 h-4 text-gray-500" />
    חברי צוות (אופציונלי)
  </label>
  <EmployeeSearchField
    employees={users.filter(user => user.role === 'employee' && user.isActive).map(user => ({
      ...user,
      departmentName: departments.find(d => d.id === user.departmentId)?.name
    }))}
    selectedEmployees={users.filter(user => departmentForm.teamMembers?.includes(user.id))}
    onEmployeeSelect={(employee) => {
      setDepartmentForm(prevState => ({
        ...prevState,
        teamMembers: [...(prevState.teamMembers || []), employee.id]
      }));
    }}
    onEmployeeRemove={(employeeId) => {
      setDepartmentForm(prevState => ({
        ...prevState,
        teamMembers: (prevState.teamMembers || []).filter(id => id !== employeeId)
      }));
    }}
    placeholder="חפש עובדים להוספה למחלקה..."
    showDepartment={true}
  />
</div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="mt-8 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingDepartment(null);
                    resetDepartmentForm();
                  }}
                  className="px-6 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  בטל
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  עדכן מחלקה
                </button>
              </div>
            </form>
          </div>
        </div>
        </div>
      )}
    </div>
  );
};

export default Departments; 